import { useState, useEffect, useCallback } from 'react';
import { marketDataService, MarketData, ApiResponse } from '../services/marketDataService';

interface UseMarketDataReturn {
  data: MarketData | null;
  loading: boolean;
  error: string | null;
  lastUpdate: string | null;
  refresh: () => Promise<void>;
}

export const useMarketData = (): UseMarketDataReturn => {
  const [data, setData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);

  // Handle real-time data updates
  const handleDataUpdate = useCallback((newData: MarketData) => {
    setData(newData);
    setLastUpdate(new Date().toLocaleString('pt-BR'));
    setError(null);
    setLoading(false);
  }, []);

  // Manual refresh function
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: ApiResponse = await marketDataService.fetchMarketData();
      
      if (response.success && response.data) {
        setData(response.data);
        setLastUpdate(new Date().toLocaleString('pt-BR'));
      } else {
        setError(response.error || 'Failed to fetch market data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Add listener for real-time updates
    marketDataService.addListener(handleDataUpdate);
    
    // Test API connection on first load
    marketDataService.testApiConnection();
    
    // Initial data fetch
    refresh();
    
    // Start real-time updates
    marketDataService.startRealTimeUpdates();

    // Cleanup
    return () => {
      marketDataService.removeListener(handleDataUpdate);
      marketDataService.stopRealTimeUpdates();
    };
  }, [handleDataUpdate, refresh]);

  return {
    data,
    loading,
    error,
    lastUpdate,
    refresh
  };
};
